// =============================================================================
// features/tasks/TaskStatusBadge.tsx — Badge visual do status de uma tarefa.
//
// Mapeia TaskStatus → variante do Badge canônico (DS §9.5).
//
// F32-S02: o banco (db/schema/tasks.ts, F15-S03) tem só 3 status —
// 'open' | 'done' | 'cancelled'. NÃO existe 'in_progress'. "Assumida" é
// derivada de claimedBy !== null, não de um status fantasma — por isso este
// componente aceita `claimedBy` opcionalmente e ajusta o rótulo/variante
// visual quando a tarefa está aberta E já foi assumida por alguém.
// =============================================================================

import type { TaskStatus } from '@elemento/shared-schemas';
import * as React from 'react';

import { Badge } from '../../components/ui/Badge';
import type { BadgeVariant } from '../../components/ui/Badge';

interface TaskStatusBadgeProps {
  status: TaskStatus;
  /** UUID de quem assumiu a tarefa, ou null/undefined = não assumida. */
  claimedBy?: string | null;
  className?: string;
}

const statusVariant: Record<TaskStatus, BadgeVariant> = {
  open: 'info',
  done: 'success',
  cancelled: 'neutral',
};

const statusLabel: Record<TaskStatus, string> = {
  open: 'Aberta',
  done: 'Concluída',
  cancelled: 'Cancelada',
};

/**
 * Badge de status de tarefa — usa o Badge canônico do DS (§9.5).
 * Quando `status === 'open'` e `claimedBy` está preenchido, exibe "Em
 * andamento" (variante warning) — puramente visual, derivado de claimedBy,
 * sem introduzir um status novo no contrato.
 */
export function TaskStatusBadge({
  status,
  claimedBy = null,
  className,
}: TaskStatusBadgeProps): React.JSX.Element {
  const isClaimedOpen = status === 'open' && claimedBy !== null;
  const variant: BadgeVariant = isClaimedOpen ? 'warning' : statusVariant[status];
  const label = isClaimedOpen ? 'Em andamento' : statusLabel[status];

  // Spread condicional para respeitar exactOptionalPropertyTypes do tsconfig
  const optionalProps = className !== undefined ? { className } : ({} as Record<string, never>);
  return (
    <Badge variant={variant} {...optionalProps}>
      {label}
    </Badge>
  );
}
